/**
 * React Query keys for reputation dashboards, competitors, and reviews.
 */
export const reputationQueryKeys = {
  /** Prefix: all reputation caches. */
  root: ['reputation'] as const,
  overview: (
    workspaceId: string | undefined,
    locationId: string | null = null,
    from: string | null = null,
    to: string | null = null,
    locale: string | undefined = undefined,
  ) =>
    [
      'reputation',
      'overview',
      workspaceId,
      locationId,
      from,
      to,
      locale,
    ] as const,
  drivers: (
    workspaceId: string | undefined,
    locationId: string | null = null,
  ) => ['reputation', 'drivers', workspaceId, locationId] as const,
  analytics: (
    workspaceId: string | undefined,
    locationId: string | null = null,
    from: string | null = null,
    to: string | null = null,
  ) => ['reputation', 'analytics', workspaceId, locationId, from, to] as const,
  channelLocations: (
    workspaceId: string | undefined,
    locationId: string | null = null,
  ) => ['reputation', 'channel-locations', workspaceId, locationId] as const,
  /** Prefix for channel-locations (any workspace / scope). */
  channelLocationsRoot: ['reputation', 'channel-locations'] as const,
  /** Prefix for analytics (any workspace / scope). */
  analyticsRoot: ['reputation', 'analytics'] as const,
  presets: (workspaceId: string | undefined) =>
    ['reputation', 'presets', workspaceId] as const,
  summary: (
    workspaceId: string | undefined,
    locationId: string | null = null,
  ) => ['reputation', 'summary', workspaceId, locationId] as const,
  competitorsRoot: ['reputation', 'competitors'] as const,
  overviewRoot: ['reputation', 'overview'] as const,
  competitorsCompare: (
    workspaceId: string | undefined,
    locationId: string | null = null,
    from: string | null = null,
    to: string | null = null,
    locale: string | undefined = undefined,
  ) =>
    [
      'reputation',
      'competitors',
      'compare',
      workspaceId,
      locationId,
      from,
      to,
      locale,
    ] as const,
  competitorsList: (
    workspaceId: string | undefined,
    locationId: string | undefined,
    locale: string | undefined,
  ) =>
    [
      'reputation',
      'competitors',
      'list',
      workspaceId,
      locationId,
      locale,
    ] as const,
  competitorsNearby: (
    workspaceId: string | undefined,
    locationId: string | undefined,
    locale: string | undefined,
    radiusMeters: number | undefined = undefined,
  ) =>
    [
      'reputation',
      'competitors',
      'nearby',
      workspaceId,
      locationId,
      locale,
      radiusMeters,
    ] as const,
  competitorsSummary: (
    workspaceId: string | undefined,
    locationId: string | undefined,
    locale: string | undefined,
  ) =>
    [
      'reputation',
      'competitors',
      'summary',
      workspaceId,
      locationId,
      locale,
    ] as const,
} as const;

export const reviewsQueryKeys = {
  all: (workspaceId: string | undefined) => ['reviews', workspaceId] as const,
  list: (
    workspaceId: string | undefined,
    locationIds: unknown,
    sentiments: unknown,
    aspects: unknown,
    responded: unknown,
    ratings: unknown,
    search: string,
    page: number,
    pageSize: number,
    focusReviewId: string | undefined,
  ) =>
    [
      'reviews',
      workspaceId,
      locationIds,
      sentiments,
      aspects,
      responded,
      ratings,
      search,
      page,
      pageSize,
      focusReviewId,
    ] as const,
  preview: (
    workspaceId: string | undefined,
    locationId: string | null | undefined,
  ) => ['reviews', workspaceId, locationId] as const,
} as const;
